/**
 * CadenceEnginePage
 * Node: N#1079  BRDs: 1421–1431
 *
 * Operator dashboard for the cadence-engine tick loop:
 *  - Lock state (BRD 2/7)
 *  - Run Now / Dry-Run (BRDs 10, 11)
 *  - Recent CadenceRun history (BRD 9) with drill-down errors + projection
 *  - Engine settings (BRDs 1, 4, 6, 7, 8)
 *  - Missed-tick log (BRD 8)
 */
import React, { useCallback, useEffect, useState } from 'react';
import extractApiError from '../../utils/apiError';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import { swalPermission, confirmWarningWithHtml } from '../common/confirm';
import { usePermissions } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { SCH_GLOBAL_CADENCE } from './cadenceModuleTypes';
import {
  EngineSettings,
  CadenceRun,
  EngineLockInfo,
  MissedTickEntry,
  TickInterval,
  CatchUpPolicy,
  TriggerSource,
  PredicateEvalPolicy,
  ApplicabilityEvaluation,
  DueDateComputation,
  EngineSubscription,
  AnchorState,
  ShiftOutcome,
  EventKeyConstruction,
  RegisterCheck,
  HandlerFire,
  HandlerRegistration,
  DeadLetterEntry,
  DlqStatus,
  VersionResolution,
  FireLogEntry,
  FireLogSourceRecon,
  MidPeriodTransition,
  CatchUpAttempt,
  getEngineSettings,
  updateEngineSettings,
  runEngineNow,
  listEngineRuns,
  getEngineRun,
  getEngineLock,
  forceReleaseEngineLock,
  listMissedTicks,
  listRunApplicabilityEvaluations,
  listRunDueDateComputations,
  listRunShiftOutcomes,
  listRunEventKeyConstructions,
  listRunRegisterChecks,
  listRunHandlerFires,
  listHandlers,
  runHandlerContractTest,
  revokeHandler,
  listDlq,
  dlqRefire,
  dlqWaive,
  dlqArchive,
  processRetryQueue,
  listRunVersionResolutions,
  listRunFireLog,
  promoteQueuedVersions,
  listMidPeriodTransitions,
  skipMidPeriodTransition,
  backfillMidPeriodTransition,
  listCatchUpAttempts,
  listEngineSubscriptions,
  refreshSubscriptions,
  clearSubscriptionError,
  listAnchorState,
} from '../../services/cadenceService';

const CadenceEnginePage: React.FC = () => {
  const { formatDate, formatDateTime } = useEntitySettings();
  const FMT = (s?: string | null) => (s ? formatDateTime(s) : '—');
  // The standalone Cadence Engine is gated by the global cadence activity.
  // Every engine mutation (run/dry-run, force-release, promote, save settings)
  // is also enforced server-side via _enforce_cadence_modify / engine guards.
  const { check, loading: permLoading } = usePermissions();
  const canModify = check(SCH_GLOBAL_CADENCE, 'view+modify').allowed;
  const canView = check(SCH_GLOBAL_CADENCE, 'view').allowed;
  const [settings, setSettings] = useState<EngineSettings | null>(null);
  const [draft, setDraft] = useState<EngineSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [lock, setLock] = useState<EngineLockInfo | null>(null);
  const [runs, setRuns] = useState<CadenceRun[]>([]);
  const [missed, setMissed] = useState<MissedTickEntry[]>([]);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const setError = (m: string) => { if (m) setToast({ type: 'error', message: m }); };
  const setInfo = (m: string) => { if (m) setToast({ type: 'success', message: m }); };
  const [filter, setFilter] = useState<TriggerSource | ''>('');
  const [selected, setSelected] = useState<CadenceRun | null>(null);
  const [selectedEvals, setSelectedEvals] = useState<ApplicabilityEvaluation[]>([]);
  const [selectedDueDates, setSelectedDueDates] = useState<DueDateComputation[]>([]);
  const [selectedShifts, setSelectedShifts] = useState<ShiftOutcome[]>([]);
  const [selectedEventKeys, setSelectedEventKeys] = useState<EventKeyConstruction[]>([]);
  const [selectedRegisterChecks, setSelectedRegisterChecks] = useState<RegisterCheck[]>([]);
  const [selectedHandlerFires, setSelectedHandlerFires] = useState<HandlerFire[]>([]);
  const [selectedVersionResolutions, setSelectedVersionResolutions] = useState<VersionResolution[]>([]);
  const [selectedFireLog, setSelectedFireLog] = useState<FireLogEntry[]>([]);
  const [sourceRecon, setSourceRecon] = useState<FireLogSourceRecon[]>([]);
  const [handlers, setHandlers] = useState<HandlerRegistration[]>([]);
  const [handlerBusy, setHandlerBusy] = useState<number | null>(null);
  const [midPeriod, setMidPeriod] = useState<MidPeriodTransition[]>([]);
  const [midPeriodBusy, setMidPeriodBusy] = useState<number | null>(null);
  const [catchUpAttempts, setCatchUpAttempts] = useState<CatchUpAttempt[]>([]);
  const [dlq, setDlq] = useState<DeadLetterEntry[]>([]);
  const [dlqFilter, setDlqFilter] = useState<DlqStatus | 'all'>('active');
  const [dlqBusy, setDlqBusy] = useState<number | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [subscriptions, setSubscriptions] = useState<EngineSubscription[]>([]);
  const [anchorState, setAnchorState] = useState<AnchorState[]>([]);
  const [refreshBusy, setRefreshBusy] = useState(false);

  const load = useCallback(async () => {
    if (!canView) return;
    setError('');
    try {
      const [s, l, r, m, subs, anchors, hs, dl, mp, cu] = await Promise.all([
        getEngineSettings(),
        getEngineLock(),
        listEngineRuns(filter || undefined, 50),
        listMissedTicks(50),
        listEngineSubscriptions(),
        listAnchorState(),
        listHandlers(),
        listDlq(dlqFilter, 100),
        listMidPeriodTransitions({ status: 'pending' }),
        listCatchUpAttempts({ limit: 25 }),
      ]);
      setSettings(s);
      setDraft(s);
      setLock(l);
      setRuns(r);
      setMissed(m);
      setSubscriptions(subs);
      setAnchorState(anchors);
      setHandlers(hs);
      setDlq(dl);
      setMidPeriod(mp);
      setCatchUpAttempts(cu);
    } catch (e: any) {
      setError(extractApiError(e, 'Failed to load engine state.'));
    }
  }, [filter, dlqFilter, canView]);

  const handleRefreshSubs = async () => {
    setError(''); setInfo(''); setRefreshBusy(true);
    try {
      const r = await refreshSubscriptions();
      setInfo(`Replayed ${r.occurrences_replayed} occurrence(s) within ${r.window_days}d; updated ${r.affected_rule_ids.length} rule(s).`
        + (r.unrecovered_old ? ` ${r.unrecovered_old} older event(s) flagged as unrecovered.` : ''));
      await load();
    } catch (e: any) {
      setError(extractApiError(e, 'Refresh subscriptions failed.'));
    } finally {
      setRefreshBusy(false);
    }
  };

  const handleClearSubError = async (id: number) => {
    try {
      await clearSubscriptionError(id);
      await load();
    } catch (e: any) {
      setError(extractApiError(e, 'Clear error failed.'));
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleRun = async (commit: boolean) => {
    if (!canModify) { swalPermission('You do not have permission to run cadence.'); return; }
    setError(''); setInfo(''); setRunning(true);
    try {
      const r = await runEngineNow(commit);
      setInfo(commit
        ? `Engine run #${r.id} ${r.status} — ${r.rules_evaluated} rule(s), ${r.instances_created} created, ${r.instances_skipped} skipped, ${r.rules_failed} failed.`
        : `Dry-run #${r.id} ${r.status} — projection captured; would-create=${r.projection?.would_create.length || 0}, would-skip=${r.projection?.would_skip.length || 0}, would-shift=${r.projection?.would_shift.length || 0}.`);
      await load();
      const fresh = await getEngineRun(r.id);
      setSelected(fresh);
    } catch (e: any) {
      setError(extractApiError(e, 'Engine run failed.'));
    } finally {
      setRunning(false);
    }
  };

  const handleForceRelease = async () => {
    if (!canModify) { swalPermission('You do not have permission to modify cadence.'); return; }
    const ok = await confirmWarningWithHtml({
      title: 'Force-release the engine lock?',
      html: 'This should only be used when a previous tick is stuck.',
    });
    if (!ok) return;
    try {
      await forceReleaseEngineLock();
      setInfo('Engine lock force-released.');
      await load();
    } catch (e: any) {
      setError(extractApiError(e, 'Force-release failed.'));
    }
  };

  const handleSaveSettings = async () => {
    if (!canModify) { swalPermission('You do not have permission to modify cadence.'); return; }
    if (!draft) return;
    setError(''); setInfo(''); setSavingSettings(true);
    try {
      const saved = await updateEngineSettings({
        tick_interval: draft.tick_interval,
        tick_time_of_day: draft.tick_time_of_day,
        tick_time_zone: draft.tick_time_zone,
        is_enabled: draft.is_enabled,
        concurrency_cap: draft.concurrency_cap,
        per_rule_throttle_default: draft.per_rule_throttle_default,
        max_lock_duration_seconds: draft.max_lock_duration_seconds,
        catch_up_policy: draft.catch_up_policy,
        catch_up_window_days: draft.catch_up_window_days,
        predicate_evaluation_policy: draft.predicate_evaluation_policy,
        retry_max_attempts: draft.retry_max_attempts,
        retry_backoff_minutes: draft.retry_backoff_minutes,
        dlq_alert_mode: draft.dlq_alert_mode,
        fire_log_retention_years: draft.fire_log_retention_years,
      });
      setSettings(saved);
      setDraft(saved);
      setInfo('Engine settings saved.');
    } catch (e: any) {
      setError(extractApiError(e, 'Save settings failed.'));
    } finally {
      setSavingSettings(false);
    }
  };

  const lockActive = (lock?.is_locked ?? 0) === 1;

  if (!permLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to the cadence engine." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">

      <div className="netbody-header">
        <div className={'engine-lock-banner ' + (lockActive ? 'locked' : 'unlocked')}>
          <div className="d-flex">
            <strong>{lockActive ? '🔒 Lock held' : '🟢 Lock free'}</strong>
            <div className="lock-meta">
              {lockActive && <span>by <code>{lock?.locked_by || '—'}</code></span>}
              {lockActive && <span>since {FMT(lock?.locked_at)}</span>}
              {lockActive && <span>expires {FMT(lock?.expires_at)}</span>}
              {!lockActive && lock?.last_release && <span>last release {FMT(lock.last_release)}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <Button variant="fill" data-testid="BTN_engine_run_now" onClick={() => handleRun(true)} disabled={running || lockActive} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}>
            {running ? 'Running…' : '▶ Run Engine Now'}
          </Button>
          <Button variant="secondary-outline" data-testid="BTN_engine_dry_run" onClick={() => handleRun(false)} disabled={running || lockActive} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}>
            Dry-Run
          </Button>
          <Button variant="delete-outline" data-testid="BTN_engine_force_release" onClick={handleForceRelease} disabled={!lockActive} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}>
            Force-release lock
          </Button>
          {/* N#1092 BRD 5 — promote Queued versions whose effective_from has arrived */}
          <Button variant="secondary-outline" data-testid="BTN_engine_promote_versions"
            className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}
            onClick={async () => {
              if (!canModify) { swalPermission('You do not have permission to modify cadence.'); return; }
              try {
                const promoted = await promoteQueuedVersions();
                setInfo(`Promoted ${promoted.length} queued version(s) to Active.`);
                await load();
              } catch (e: any) {
                setError(extractApiError(e, 'Promote failed.'));
              }
            }}>
            Promote queued versions
          </Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden space-y-4">
          {/* Run history */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Recent runs</h3>
                <div className="subtle">CadenceRun audit log — every scheduled / manual / dry-run tick.</div>
              </div>
              <div className="engine-actions flex items-center gap-2">
                <FormControl.SearchSelect
                  value={filter}
                  onChange={v => setFilter((v as TriggerSource | null) ?? '')}
                  options={[
                    { value: '', label: 'All sources' },
                    { value: 'scheduled', label: 'Scheduled' },
                    { value: 'manual', label: 'Manual' },
                    { value: 'dry-run', label: 'Dry-run' },
                  ]}
                  className="filter-status"
                  noMargin
                  data-testid="SEL_engine_runs_filter"
                />
                <Button variant="secondary-outline" data-testid="BTN_engine_runs_refresh" onClick={load}>Refresh</Button>
              </div>
            </div>

            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Source</th>
                      <th className="table-list-th">Started</th>
                      <th className="table-list-th">Duration</th>
                      <th className="table-list-th">Evaluated</th>
                      <th className="table-list-th">Created</th>
                      <th className="table-list-th">Skipped</th>
                      <th className="table-list-th">Failed</th>
                      <th className="table-list-th table-list-cell--status">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.length === 0 && (
                      <tr>
                        <td colSpan={9} className="table-list-empty-cell">
                          <NoDataFound
                            title="No runs yet"
                            message='Click "Run Engine Now" or "Dry-Run" to start.'
                          />
                        </td>
                      </tr>
                    )}
                    {runs.map((r, idx) => (
                      <tr key={r.id} className="table-list-row" data-testid={`BTN_engine_run_row_${r.id}_open`} onClick={async () => {
                        const fresh = await getEngineRun(r.id);
                        setSelected(fresh);
                        try { setSelectedEvals(await listRunApplicabilityEvaluations(r.id)); }
                        catch { setSelectedEvals([]); }
                        try { setSelectedDueDates(await listRunDueDateComputations(r.id)); }
                        catch { setSelectedDueDates([]); }
                        try { setSelectedShifts(await listRunShiftOutcomes(r.id)); }
                        catch { setSelectedShifts([]); }
                        try { setSelectedEventKeys(await listRunEventKeyConstructions(r.id)); }
                        catch { setSelectedEventKeys([]); }
                        try { setSelectedRegisterChecks(await listRunRegisterChecks(r.id)); }
                        catch { setSelectedRegisterChecks([]); }
                        try { setSelectedHandlerFires(await listRunHandlerFires(r.id)); }
                        catch { setSelectedHandlerFires([]); }
                        try { setSelectedVersionResolutions(await listRunVersionResolutions(r.id)); }
                        catch { setSelectedVersionResolutions([]); }
                        try { setSelectedFireLog(await listRunFireLog(r.id)); }
                        catch { setSelectedFireLog([]); }
                      }}>
                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                        <td className="table-list-cell"><span className={'run-source s-' + r.trigger_source}>{r.trigger_source}</span></td>
                        <td className="table-list-cell">{FMT(r.started_at)}</td>
                        <td className="table-list-cell">{r.duration_ms != null ? `${r.duration_ms} ms` : '—'}</td>
                        <td className="table-list-cell">{r.rules_evaluated}</td>
                        <td className="table-list-cell">{r.instances_created}</td>
                        <td className="table-list-cell">{r.instances_skipped}</td>
                        <td className="table-list-cell">{r.rules_failed}</td>
                        <td className="table-list-cell"><span className={'run-status s-' + r.status}>{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Run detail panel */}
            {selected && (
              <div className="run-detail">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4>Run #{selected.id} — {selected.trigger_source} · {selected.status}</h4>
                  <Button variant="secondary-outline" data-testid="BTN_engine_run_detail_close" onClick={() => setSelected(null)}>
                    Close
                  </Button>
                </div>

                <div className="run-metrics">
                  <div className="metric"><div className="v">{selected.rules_evaluated}</div><div className="k">Evaluated</div></div>
                  <div className="metric"><div className="v">{selected.rules_succeeded}</div><div className="k">Succeeded</div></div>
                  <div className="metric"><div className="v">{selected.rules_failed}</div><div className="k">Failed</div></div>
                  <div className="metric"><div className="v">{selected.fires_emitted}</div><div className="k">Fires</div></div>
                  <div className="metric"><div className="v">{selected.instances_created}</div><div className="k">Created</div></div>
                  <div className="metric"><div className="v">{selected.instances_skipped}</div><div className="k">Skipped</div></div>
                  <div className="metric"><div className="v">{selected.duration_ms ?? '—'}</div><div className="k">Duration (ms)</div></div>
                  <div className="metric"><div className="v">{selected.commit_writes ? 'yes' : 'no'}</div><div className="k">Commit writes</div></div>
                </div>

                {selected.skip_reason && (
                  <div className="run-errors">
                    <strong>Skip reason:</strong> {selected.skip_reason}
                  </div>
                )}

                {selected.errors && selected.errors.length > 0 && (
                  <div className="run-errors">
                    <strong>Per-rule errors ({selected.errors.length}):</strong>
                    <pre>{JSON.stringify(selected.errors, null, 2)}</pre>
                  </div>
                )}

                {selected.projection && (
                  <div className="run-projection">
                    <h5>Dry-run projection</h5>
                    <div>
                      <strong>Would create ({selected.projection.would_create.length}):</strong>
                      <pre>{JSON.stringify(selected.projection.would_create.slice(0, 20), null, 2)}</pre>
                    </div>
                    <div>
                      <strong>Would skip ({selected.projection.would_skip.length}):</strong>
                      <pre>{JSON.stringify(selected.projection.would_skip.slice(0, 20), null, 2)}</pre>
                    </div>
                    <div>
                      <strong>Would shift ({selected.projection.would_shift.length}):</strong>
                      <pre>{JSON.stringify(selected.projection.would_shift.slice(0, 20), null, 2)}</pre>
                    </div>
                  </div>
                )}

                {/* N#1090 — Version resolutions panel (FIRST per-rule phase) */}
                {selectedVersionResolutions && selectedVersionResolutions.length > 0 && (
                  <div className="run-projection">
                    <h5>Version resolutions ({selectedVersionResolutions.length})</h5>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
                      BRD 1 — runs FIRST per rule. Drives every downstream phase.
                      <span className="run-status s-succeeded" style={{ marginLeft: 6 }}>resolved</span>
                      picks <code>live</code> (current rule) or <code>snapshot</code> (historical
                      catch-up). <span className="run-status s-partial" style={{ marginLeft: 4 }}>no_active_version</span>
                      means the target date predates the rule (BRD 5) — skipped, not DLQ.
                    </div>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Target date</th>
                              <th className="table-list-th">Catch-up?</th>
                              <th className="table-list-th">Outcome</th>
                              <th className="table-list-th">Source</th>
                              <th className="table-list-th">Version</th>
                              <th className="table-list-th">Effective window</th>
                              <th className="table-list-th">Notes / Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedVersionResolutions.map(v => (
                              <tr key={v.id} className="table-list-row" style={
                                v.outcome === 'resolved' ? undefined :
                                  v.outcome === 'no_active_version' ? { background: '#fef9c3' } :
                                    { background: '#fee2e2' }
                              }>
                                <td className="table-list-cell">#{v.rule_id}</td>
                                <td className="table-list-cell">{formatDate(v.target_date)}</td>
                                <td className="table-list-cell">{v.is_catch_up ? <span className="run-source s-manual">yes</span> : '—'}</td>
                                <td className="table-list-cell">
                                  <span className={'run-status s-' + (
                                    v.outcome === 'resolved' ? 'succeeded' :
                                      v.outcome === 'no_active_version' ? 'partial' :
                                        'lock-expired'
                                  )}>
                                    {v.outcome}
                                  </span>
                                </td>
                                <td className="table-list-cell">
                                  {v.resolved_source ? (
                                    <span className={'run-source s-' + (
                                      v.resolved_source === 'live' ? 'scheduled' :
                                        v.resolved_source === 'snapshot' ? 'manual' :
                                          'dry-run'
                                    )}>
                                      {v.resolved_source}
                                    </span>
                                  ) : <span style={{ color: '#94a3b8' }}>—</span>}
                                </td>
                                <td className="table-list-cell">
                                  {v.resolved_version_number != null ? (
                                    <code>v{v.resolved_version_number}{v.resolved_version_id ? ` (#${v.resolved_version_id})` : ''}</code>
                                  ) : <span style={{ color: '#94a3b8' }}>—</span>}
                                </td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>
                                  {v.resolved_effective_from ?? '—'}
                                  {v.resolved_effective_until ? ` → ${v.resolved_effective_until}` : ''}
                                </td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem', color: v.error_message ? '#b91c1c' : '#475569', maxWidth: 240 }}>
                                  {v.error_message ? <strong>{v.error_message}</strong> : (v.notes || '—')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1091 — Fire Log panel (consolidated per-fire audit, BRDs 1, 2, 5, 7, 9) */}
                {selectedFireLog && selectedFireLog.length > 0 && (
                  <div className="run-projection">
                    <h5>Fire log ({selectedFireLog.length})</h5>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
                      Consolidated per-fire audit record. One row per fire attempt — including
                      <code> fired</code>, <code>skipped</code>, <code>error</code>,
                      <code> dead_lettered</code>, and dry-run <code>would_create</code> /
                      <code> would_skip</code> / <code>would_shift</code> projections (BRD 5).
                      Status transitions live in an append-only ledger — original rows are immutable (BRD 7).
                    </div>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Fire #</th>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th table-list-cell--status">Status</th>
                              <th className="table-list-th">Source</th>
                              <th className="table-list-th">Geo</th>
                              <th className="table-list-th">Period</th>
                              <th className="table-list-th">Version</th>
                              <th className="table-list-th">Occ / Retry</th>
                              <th className="table-list-th">Instance</th>
                              <th className="table-list-th">Fired at</th>
                              <th className="table-list-th">Error / Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedFireLog.map(f => (
                              <tr key={f.id} className="table-list-row" style={
                                f.fire_status === 'fired' || f.fire_status === 'succeeded' ? { background: '#f0fdf4' } :
                                  f.fire_status === 'error' || f.fire_status === 'retrying' || f.fire_status === 'dead_lettered' ? { background: '#fef2f2' } :
                                    f.fire_status === 'skipped' || f.fire_status === 'waived' ? { background: '#fef9c3' } :
                                      f.fire_status.startsWith('would_') ? { background: '#eff6ff' } :
                                        undefined
                              }>
                                <td className="table-list-cell">#{f.id}</td>
                                <td className="table-list-cell">#{f.cadence_rule_id}</td>
                                <td className="table-list-cell">
                                  <span className={'run-status s-' + (
                                    f.fire_status === 'fired' || f.fire_status === 'succeeded' ? 'succeeded' :
                                      f.fire_status === 'error' || f.fire_status === 'retrying' ? 'lock-expired' :
                                        f.fire_status === 'dead_lettered' ? 'lock-expired' :
                                          f.fire_status === 'skipped' || f.fire_status === 'waived' ? 'partial' :
                                            'skipped'
                                  )}>
                                    {f.fire_status}
                                  </span>
                                </td>
                                <td className="table-list-cell">
                                  <span className={'run-source s-' + (
                                    f.source === 'cadence' ? 'scheduled' :
                                      f.source === 'form' ? 'manual' :
                                        'dry-run'
                                  )}>
                                    {f.source}
                                  </span>
                                </td>
                                <td className="table-list-cell">{f.geography_id ?? '—'}</td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem' }}><code>{f.period_token || '—'}</code></td>
                                <td className="table-list-cell">{f.resolved_version_number != null ? <code>v{f.resolved_version_number}</code> : '—'}</td>
                                <td className="table-list-cell">{f.occurrence_number ?? '—'} / r{f.retry_count}</td>
                                <td className="table-list-cell">{f.instance_id != null ? <code>#{f.instance_id}</code> : '—'}</td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>{FMT(f.fired_at)}</td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem', maxWidth: 240, color: f.error_code ? '#b91c1c' : '#475569' }}>
                                  {f.error_code ? <strong>{f.error_code}: </strong> : null}
                                  {(f.error_message || f.skip_reason || '').slice(0, 160)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1082 — Due-date computations panel */}
                {selectedDueDates && selectedDueDates.length > 0 && (
                  <div className="run-projection">
                    <h5>Due-date computations ({selectedDueDates.length})</h5>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Element</th>
                              <th className="table-list-th">Mode</th>
                              <th className="table-list-th">Specifier / Variant</th>
                              <th className="table-list-th">Period token</th>
                              <th className="table-list-th">Anchor</th>
                              <th className="table-list-th">Computed due (unshifted)</th>
                              <th className="table-list-th">Path</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDueDates.map(d => (
                              <tr key={d.id} className="table-list-row" style={d.skipped ? { background: '#fef2f2' } : undefined}>
                                <td className="table-list-cell">#{d.rule_id}</td>
                                <td className="table-list-cell">{d.scope_element_id ?? '—'}</td>
                                <td className="table-list-cell">
                                  <span className={'run-source s-' + (d.mode === 'calendar' ? 'manual' : 'dry-run')}>
                                    {d.mode}{d.span ? ` · ${d.span}` : ''}
                                  </span>
                                </td>
                                <td className="table-list-cell"><code>{d.specifier || d.variant || '—'}</code></td>
                                <td className="table-list-cell"><code>{d.period_token || '—'}</code></td>
                                <td className="table-list-cell">
                                  {d.anchor_event_id != null ? (
                                    <>
                                      <code>#{d.anchor_event_id}</code>
                                      {d.anchor_timestamp ? <span style={{ color: '#64748b' }}> · {d.anchor_timestamp}</span> : null}
                                    </>
                                  ) : <span style={{ color: '#94a3b8' }}>—</span>}
                                </td>
                                <td className="table-list-cell">
                                  {d.skipped ? (
                                    <span style={{ color: '#b91c1c' }}>skipped</span>
                                  ) : (
                                    <strong>{formatDate(d.computed_due_date)}</strong>
                                  )}
                                  {d.skip_reason && (
                                    <div style={{ fontSize: '0.74rem', color: '#7f1d1d' }}>{d.skip_reason}</div>
                                  )}
                                </td>
                                <td className="table-list-cell" style={{ color: '#64748b', maxWidth: 280 }}>{d.path_notes || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1084 — Shift outcomes panel */}
                {selectedShifts && selectedShifts.length > 0 && (
                  <div className="run-projection">
                    <h5>Shift outcomes ({selectedShifts.length})</h5>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Element</th>
                              <th className="table-list-th">Action</th>
                              <th className="table-list-th">Outcome</th>
                              <th className="table-list-th">Original</th>
                              <th className="table-list-th">Final</th>
                              <th className="table-list-th">Δ days</th>
                              <th className="table-list-th">Reason</th>
                              <th className="table-list-th">Calendar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedShifts.map(s => (
                              <tr key={s.id} className="table-list-row" style={s.outcome === 'shifted' ? { background: '#fff7ed' } : s.outcome === 'no_shift_strict' ? { background: '#fef2f2' } : undefined}>
                                <td className="table-list-cell">#{s.rule_id}</td>
                                <td className="table-list-cell">{s.scope_element_id ?? '—'}</td>
                                <td className="table-list-cell"><code>{s.action}</code></td>
                                <td className="table-list-cell">
                                  <span className={'run-status s-' + (s.outcome === 'on-day' ? 'succeeded' : s.outcome === 'shifted' ? 'partial' : 'lock-expired')}>
                                    {s.outcome}
                                  </span>
                                </td>
                                <td className="table-list-cell">{formatDate(s.original_date)}</td>
                                <td className="table-list-cell"><strong>{formatDate(s.final_date)}</strong></td>
                                <td className="table-list-cell">{s.distance_days > 0 ? `+${s.distance_days}` : s.distance_days}</td>
                                <td className="table-list-cell" style={{ color: '#475569' }}>{s.reason || '—'}</td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem', color: s.calendar_available ? '#475569' : '#b91c1c' }}>
                                  {s.holiday_calendar_id != null
                                    ? (s.calendar_available
                                      ? <>#{s.holiday_calendar_id}</>
                                      : <><span title="missing — business-day only fallback">#{s.holiday_calendar_id} ⚠</span></>)
                                    : <span style={{ color: '#94a3b8' }}>—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1085 — Event-key constructions panel */}
                {selectedEventKeys && selectedEventKeys.length > 0 && (
                  <div className="run-projection">
                    <h5>Event-key constructions ({selectedEventKeys.length})</h5>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
                      Canonical 4-tuple — <code>t=tenant | et=event_type | g=geography | p=period</code>.
                      One row per (rule, scope element, occurrence). Same source obligation across rules
                      produces the same <code>event_type_id</code> (BRD 3); 'NONE' marks rule-level events without geography (BRD 4).
                    </div>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Element</th>
                              <th className="table-list-th">event_type_id</th>
                              <th className="table-list-th">period_id</th>
                              <th className="table-list-th">Source</th>
                              <th className="table-list-th">Occurrence</th>
                              <th className="table-list-th">event_key</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedEventKeys.map(k => (
                              <tr key={k.id} className="table-list-row">
                                <td className="table-list-cell">#{k.rule_id}</td>
                                <td className="table-list-cell">{k.scope_element_id ?? <span style={{ color: '#94a3b8' }}>NONE</span>}</td>
                                <td className="table-list-cell"><code>{k.event_type_id}</code></td>
                                <td className="table-list-cell"><code>{k.period_id}</code></td>
                                <td className="table-list-cell">
                                  <span className={'run-source s-' + (k.source === 'cadence' ? 'scheduled' : k.source === 'human' ? 'manual' : 'dry-run')}>
                                    {k.source}
                                  </span>
                                </td>
                                <td className="table-list-cell">{k.occurrence_as_of || '—'}</td>
                                <td className="table-list-cell" style={{ wordBreak: 'break-all', fontSize: '0.74rem', color: '#1f2937' }}>
                                  {k.event_key}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1086 — Register-check panel */}
                {selectedRegisterChecks && selectedRegisterChecks.length > 0 && (
                  <div className="run-projection">
                    <h5>Register checks ({selectedRegisterChecks.length})</h5>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
                      Single indexed lookup on <code>(entity_id, event_key)</code>.
                      <span className="run-status s-succeeded" style={{ marginLeft: 6 }}>not_exists</span> proceeds to Handler Invocation;
                      <span className="run-status s-lock-expired" style={{ marginLeft: 4 }}>exists</span> skips with BRD 5 audit context;
                      <span className="run-status s-partial" style={{ marginLeft: 4 }}>race_detected</span> is the handler-side dedup path (BRD 7).
                    </div>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Element</th>
                              <th className="table-list-th">Result</th>
                              <th className="table-list-th">event_key</th>
                              <th className="table-list-th">Existing #</th>
                              <th className="table-list-th">Source</th>
                              <th className="table-list-th">Originator</th>
                              <th className="table-list-th">Dry-run</th>
                              <th className="table-list-th">Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRegisterChecks.map(c => (
                              <tr key={c.id} className="table-list-row" style={c.result === 'exists' ? { background: '#fef2f2' } : c.result === 'race_detected' ? { background: '#fff7ed' } : undefined}>
                                <td className="table-list-cell">#{c.rule_id}</td>
                                <td className="table-list-cell">{c.scope_element_id ?? '—'}</td>
                                <td className="table-list-cell">
                                  <span className={'run-status s-' + (c.result === 'not_exists' ? 'succeeded' : c.result === 'race_detected' ? 'partial' : 'lock-expired')}>
                                    {c.result}
                                  </span>
                                </td>
                                <td className="table-list-cell" style={{ wordBreak: 'break-all', fontSize: '0.74rem', color: '#1f2937' }}>
                                  {c.event_key}
                                </td>
                                <td className="table-list-cell">{c.existing_instance_id != null ? <code>#{c.existing_instance_id}</code> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                <td className="table-list-cell">
                                  {c.existing_instance_source
                                    ? <span className={'run-source s-' + (c.existing_instance_source === 'cadence' ? 'scheduled' : c.existing_instance_source === 'form' ? 'manual' : 'dry-run')}>
                                      {c.existing_instance_source}
                                    </span>
                                    : <span style={{ color: '#94a3b8' }}>—</span>}
                                </td>
                                <td className="table-list-cell" style={{ fontSize: '0.78rem', color: '#475569' }}>
                                  {c.existing_source_rule_id != null && <>rule #{c.existing_source_rule_id}</>}
                                  {c.existing_source_user_id != null && <>user #{c.existing_source_user_id}</>}
                                  {c.existing_source_rule_id == null && c.existing_source_user_id == null && <span style={{ color: '#94a3b8' }}>—</span>}
                                </td>
                                <td className="table-list-cell">{c.is_dry_run ? <span className="run-source s-dry-run">yes</span> : '—'}</td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>{formatDateTime(c.existing_created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1087 — Handler-fire queue panel */}
                {selectedHandlerFires && selectedHandlerFires.length > 0 && (
                  <div className="run-projection">
                    <h5>Handler fires ({selectedHandlerFires.length})</h5>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
                      DB-backed fire queue. Each row carries the BRD&nbsp;3 fire payload, dispatch state,
                      and BRD&nbsp;5 response capture (instance_id on success, error_code + last_error on failure).
                      Latency is measured from <code>started_at</code> to <code>finished_at</code>.
                    </div>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Queue #</th>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Element</th>
                              <th className="table-list-th">Occ #</th>
                              <th className="table-list-th">Retry #</th>
                              <th className="table-list-th table-list-cell--status">Status</th>
                              <th className="table-list-th">Instance #</th>
                              <th className="table-list-th">Attempts</th>
                              <th className="table-list-th">Latency</th>
                              <th className="table-list-th">Scheduled for</th>
                              <th className="table-list-th">Notes / Error</th>
                              <th className="table-list-th">Payload</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedHandlerFires.map(f => (
                              <tr key={f.id} className="table-list-row" style={
                                f.status === 'succeeded' ? undefined :
                                  f.status === 'retrying' || f.status === 'failed' ? { background: '#fee2e2' } :
                                    f.status === 'dead_letter' ? { background: '#fecaca' } :
                                      { background: '#fef9c3' }
                              }>
                                <td className="table-list-cell">#{f.id}</td>
                                <td className="table-list-cell">#{f.rule_id}</td>
                                <td className="table-list-cell">{f.scope_element_id ?? '—'}</td>
                                <td className="table-list-cell">{f.occurrence_number}</td>
                                <td className="table-list-cell">{f.retry_count}</td>
                                <td className="table-list-cell">
                                  <span className={'run-status s-' + (
                                    f.status === 'succeeded' ? 'succeeded' :
                                      f.status === 'retrying' || f.status === 'failed' ? 'lock-expired' :
                                        f.status === 'dead_letter' ? 'lock-expired' :
                                          'partial'
                                  )}>
                                    {f.status}
                                  </span>
                                </td>
                                <td className="table-list-cell">{f.instance_id != null ? <code>#{f.instance_id}</code> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                <td className="table-list-cell">{f.attempts}</td>
                                <td className="table-list-cell">{f.latency_ms != null ? `${f.latency_ms} ms` : '—'}</td>
                                <td className="table-list-cell">{f.scheduled_for}</td>
                                <td className="table-list-cell" style={{ fontSize: '0.74rem', color: f.last_error ? '#b91c1c' : '#475569', maxWidth: 220 }}>
                                  {f.last_error
                                    ? <><strong>{f.error_code}</strong>: {f.last_error}</>
                                    : (f.response_notes || '—')}
                                </td>
                                <td className="table-list-cell">
                                  <details>
                                    <summary style={{ cursor: 'pointer', color: '#4f46e5', fontSize: '0.74rem' }}>view</summary>
                                    <pre style={{ fontSize: '0.7rem', maxWidth: 360, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f8fafc', padding: 6, marginTop: 4, borderRadius: 4 }}>
                                      {JSON.stringify(f.payload, null, 2)}
                                    </pre>
                                  </details>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1087 BRD 6 — dry-run "would invoke" projection panel */}
                {selected.projection?.would_invoke && selected.projection.would_invoke.length > 0 && (
                  <div className="run-projection">
                    <h5>Would invoke (dry-run, {selected.projection.would_invoke.length})</h5>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 6 }}>
                      Full BRD&nbsp;3 fire-contract payloads the handler <em>would</em> have received.
                      No queue write, no register write — projection only.
                    </div>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Element</th>
                              <th className="table-list-th">Payload</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selected.projection.would_invoke.map((w, i) => (
                              <tr key={i} className="table-list-row">
                                <td className="table-list-cell">#{w.rule_id}</td>
                                <td className="table-list-cell">{w.location_id ?? '—'}</td>
                                <td className="table-list-cell">
                                  <pre style={{ fontSize: '0.74rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f8fafc', padding: 6, borderRadius: 4 }}>
                                    {JSON.stringify(w.payload, null, 2)}
                                  </pre>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* N#1081 — Applicability evaluations panel */}
                {selectedEvals && selectedEvals.length > 0 && (
                  <div className="run-projection">
                    <h5>Applicability evaluations ({selectedEvals.length})</h5>
                    <div className="table-list">
                      <div className="table-list-scroll">
                        <table className="table-list-table" style={{ fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th className="table-list-th">Rule</th>
                              <th className="table-list-th">Mode</th>
                              <th className="table-list-th">Element</th>
                              <th className="table-list-th">Passed</th>
                              <th className="table-list-th">Reason</th>
                              <th className="table-list-th">Threshold / Predicate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedEvals.map(e => (
                              <tr key={e.id} className="table-list-row">
                                <td className="table-list-cell">#{e.rule_id}</td>
                                <td className="table-list-cell">
                                  <span className={'run-source s-' + (e.mode === 'unconditional' ? 'scheduled' : (e.mode === 'threshold' ? 'manual' : 'dry-run'))}>
                                    {e.mode}
                                  </span>
                                </td>
                                <td className="table-list-cell">{e.scope_element_id ?? '—'}</td>
                                <td className="table-list-cell">{e.passed === true ? '✓' : e.passed === false ? '✗' : '—'}</td>
                                <td className="table-list-cell" style={{ color: '#475569' }}>{e.reason || '—'}</td>
                                <td className="table-list-cell">
                                  {e.mode === 'threshold' && (
                                <>
                                  <code>{e.threshold_used || '—'}</code>
                                  {e.value_against && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>against: <code>{e.value_against}</code></div>}
                                </>
                              )}
                              {e.mode === 'questionnaire' && (
                                <>
                                  <code>{e.predicate_text || '—'}</code>
                                  {e.submission_id != null && (
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>submission #{e.submission_id}</div>
                                  )}
                                  {(e.matched_conditions || []).length > 0 && (
                                    <details style={{ marginTop: 4 }}>
                                      <summary style={{ cursor: 'pointer', color: '#4f46e5' }}>matched conditions</summary>
                                      <ul style={{ margin: '4px 0 0 18px', fontSize: '0.75rem' }}>
                                        {(e.matched_conditions || []).map((m, i) => (
                                          <li key={i}>
                                            <code>{m.question_code} {m.operator} {m.expected}</code>{' '}
                                            — actual <code>{m.actual ?? 'null'}</code> · {m.match ? '✓' : '✗'}
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  )}
                                </>
                              )}
                                  {e.mode === 'unconditional' && <span style={{ color: '#94a3b8' }}>N/A</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Settings */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Engine settings</h3>
                <div className="subtle">Tick schedule, concurrency cap, throttle, lock duration, catch-up policy.</div>
              </div>
              <div className="engine-actions flex items-center gap-2">
                <Button variant="update" data-testid="BTN_engine_settings_save" onClick={handleSaveSettings} disabled={savingSettings || !draft} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}>
                  {savingSettings ? 'Updating...' : 'Save settings'}
                </Button>
              </div>
            </div>

            <div className="engine-section-body">
              {!draft ? <div className="empty-state">Loading…</div> : (
                <div className="engine-settings-grid">
                  <FormControl.SearchSelect
                    label="Tick interval"
                    value={draft.tick_interval}
                    onChange={v => setDraft({ ...draft, tick_interval: v as TickInterval })}
                    options={[
                      { value: 'hourly', label: 'Hourly' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                    ]}
                    data-testid="SEL_engine_tick_interval"
                  />
                  <FormControl.Input
                    label="Tick time-of-day (HH:MM)"
                    type="text"
                    value={draft.tick_time_of_day}
                    onChange={e => setDraft({ ...draft, tick_time_of_day: e.target.value })}
                    data-testid="INP_engine_tick_time_of_day"
                  />
                  <FormControl.Input
                    label="Tick time zone (IANA)"
                    type="text"
                    value={draft.tick_time_zone}
                    onChange={e => setDraft({ ...draft, tick_time_zone: e.target.value })}
                    data-testid="INP_engine_tick_time_zone"
                  />
                  <FormControl.SearchSelect
                    label="Engine enabled"
                    value={String(draft.is_enabled)}
                    onChange={v => setDraft({ ...draft, is_enabled: parseInt(String(v)) })}
                    options={[
                      { value: '1', label: 'Yes' },
                      { value: '0', label: 'No' },
                    ]}
                    data-testid="SEL_engine_is_enabled"
                  />
                  <FormControl.Input
                    label="Concurrency cap"
                    type="number"
                    min={1}
                    value={draft.concurrency_cap}
                    onChange={e => setDraft({ ...draft, concurrency_cap: parseInt(e.target.value || '1') })}
                    data-testid="INP_engine_concurrency_cap"
                  />
                  <FormControl.Input
                    label="Default per-rule throttle (fires/sec)"
                    type="number"
                    min={1}
                    value={draft.per_rule_throttle_default}
                    onChange={e => setDraft({ ...draft, per_rule_throttle_default: parseInt(e.target.value || '1') })}
                    data-testid="INP_engine_throttle_default"
                  />
                  <FormControl.Input
                    label="Max lock duration (seconds)"
                    type="number"
                    min={30}
                    value={draft.max_lock_duration_seconds}
                    onChange={e => setDraft({ ...draft, max_lock_duration_seconds: parseInt(e.target.value || '30') })}
                    data-testid="INP_engine_max_lock_duration"
                  />
                  <FormControl.SearchSelect
                    label="Catch-up policy"
                    value={draft.catch_up_policy}
                    onChange={v => setDraft({ ...draft, catch_up_policy: v as CatchUpPolicy })}
                    options={[
                      { value: 'skip_with_alert', label: 'Skip with alert' },
                      { value: 'backdate_within_window', label: 'Backdate within window' },
                    ]}
                    data-testid="SEL_engine_catch_up_policy"
                  />
                  <FormControl.Input
                    label="Catch-up window (days)"
                    type="number"
                    min={0}
                    value={draft.catch_up_window_days}
                    onChange={e => setDraft({ ...draft, catch_up_window_days: parseInt(e.target.value || '0') })}
                    data-testid="INP_engine_catch_up_window_days"
                  />
                  <FormControl.SearchSelect
                    label="Predicate Evaluation Policy"
                    value={draft.predicate_evaluation_policy}
                    onChange={v => setDraft({ ...draft, predicate_evaluation_policy: v as PredicateEvalPolicy })}
                    options={[
                      { value: 'every_tick', label: 'Every tick' },
                      { value: 'on_attribute_change', label: 'On attribute change' },
                      { value: 'both', label: 'Both' },
                    ]}
                    data-testid="SEL_engine_predicate_policy"
                  />
                  {/* N#1089 retry / DLQ policy (global only — BRD 7) */}
                  <FormControl.Input
                    label="Retry max attempts"
                    type="number"
                    min={0}
                    max={10}
                    value={draft.retry_max_attempts ?? 3}
                    onChange={e => setDraft({ ...draft, retry_max_attempts: parseInt(e.target.value || '0') })}
                    helper="Retry count after the initial attempt. BRD 7: global only."
                    data-testid="INP_engine_retry_max_attempts"
                  />
                  <FormControl.Input
                    label="Retry backoff (minutes, csv)"
                    type="text"
                    value={draft.retry_backoff_minutes ?? '5,15,60'}
                    onChange={e => setDraft({ ...draft, retry_backoff_minutes: e.target.value })}
                    helper={<>e.g. <code>5,15,60</code> for 5m, 15m, 60m</>}
                    data-testid="INP_engine_retry_backoff"
                  />
                  <FormControl.SearchSelect
                    label="DLQ alert mode"
                    value={draft.dlq_alert_mode ?? 'immediate'}
                    onChange={v => setDraft({ ...draft, dlq_alert_mode: v as 'immediate' | 'aggregate' | 'none' })}
                    options={[
                      { value: 'immediate', label: 'Immediate (one alert per entry)' },
                      { value: 'aggregate', label: 'Aggregate (daily digest)' },
                      { value: 'none', label: 'None' },
                    ]}
                    data-testid="SEL_engine_dlq_alert_mode"
                  />
                  {/* N#1091 BRD 6 — Fire-log retention */}
                  <FormControl.Input
                    label="Fire log retention (years)"
                    type="number"
                    min={0}
                    max={50}
                    value={draft.fire_log_retention_years ?? 7}
                    onChange={e => setDraft({ ...draft, fire_log_retention_years: parseInt(e.target.value || '7') })}
                    helper="Default 7 years for compliance audit. Archival to cold storage is Bead-5 future work."
                    data-testid="INP_engine_fire_log_retention"
                  />
                </div>
              )}
            </div>
          </section>

          {/* Subscriptions (N#1083) */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Subscriptions</h3>
                <div className="subtle">
                  Continuous subscriptions to Event Library / Questionnaire Library / Obligation
                  Register per rule (BRDs 1, 7, 8).
                </div>
              </div>
              <div className="engine-actions flex items-center gap-2">
                <Button variant="secondary-outline" data-testid="BTN_engine_subs_replay" onClick={handleRefreshSubs} disabled={refreshBusy}>
                  {refreshBusy ? 'Replaying…' : 'Replay recent events'}
                </Button>
              </div>
            </div>
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th">Rule</th>
                      <th className="table-list-th">Kind</th>
                      <th className="table-list-th">Source</th>
                      <th className="table-list-th">Last event</th>
                      <th className="table-list-th">Heartbeat</th>
                      <th className="table-list-th table-list-cell--status">Status</th>
                      <th className="table-list-th">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map(s => (
                      <tr key={s.id} className="table-list-row">
                        <td className="table-list-cell">#{s.rule_id} <span style={{ color: '#94a3b8', fontSize: '0.74rem' }}>{s.rule_status || ''}</span></td>
                        <td className="table-list-cell"><span className={'run-source s-' + (s.source_kind === 'event' ? 'manual' : s.source_kind === 'questionnaire' ? 'dry-run' : 'scheduled')}>{s.source_kind}</span></td>
                        <td className="table-list-cell"><code>{s.source_label || `#${s.source_ref_id}`}</code></td>
                        <td className="table-list-cell">{FMT(s.last_event_at)}</td>
                        <td className="table-list-cell">{FMT(s.last_heartbeat_at)}{s.stale_for_days != null ? <span style={{ color: '#64748b', fontSize: '0.74rem' }}> · {s.stale_for_days}d gap</span> : null}</td>
                        <td className="table-list-cell">
                          {s.is_paused === 1 ? (
                            <span className="run-status s-skipped">paused</span>
                          ) : s.is_stale ? (
                            <span className="run-status s-partial">stale</span>
                          ) : (
                            <span className="run-status s-succeeded">healthy</span>
                          )}
                          {s.pause_reason && <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{s.pause_reason}</div>}
                        </td>
                        <td className="table-list-cell">
                          {s.error_count > 0 ? (
                            <>
                              <span style={{ color: '#b91c1c', fontWeight: 600 }}>{s.error_count}</span>
                              <span style={{ marginLeft: 6, display: 'inline-block' }}>
                                <Button variant="secondary-outline" data-testid={`BTN_engine_subs_row_${s.id}_clear_error`} onClick={() => handleClearSubError(s.id)}>clear</Button>
                              </span>
                            </>
                          ) : <span style={{ color: '#94a3b8' }}>0</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {subscriptions.length === 0 && (
                <NoDataFound
                  title="No subscriptions registered yet"
                  message="Activate a Mode-B rule or a questionnaire-driven rule to register one."
                />
              )}
            </div>
          </section>

          {/* Anchor state cache (N#1083 BRD 3) */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Anchor state</h3>
                <div className="subtle">Latest qualifying anchor per (rule, event) — Mode B reads from here at fire time.</div>
              </div>
            </div>
            <div className="table-list">
              <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th">Rule</th>
                    <th className="table-list-th">Event</th>
                    <th className="table-list-th">Location</th>
                    <th className="table-list-th">Occurred</th>
                    <th className="table-list-th">Completion</th>
                    <th className="table-list-th">Due</th>
                    <th className="table-list-th">History</th>
                    <th className="table-list-th">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {anchorState.map(a => (
                    <tr key={a.id} className="table-list-row">
                      <td className="table-list-cell">#{a.rule_id}</td>
                      <td className="table-list-cell"><code>{a.event_code || `#${a.event_id}`}</code></td>
                      <td className="table-list-cell">{a.location_id ?? <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td className="table-list-cell">{formatDate(a.occurred_on)}</td>
                      <td className="table-list-cell">{formatDate(a.completion_date)}</td>
                      <td className="table-list-cell">{formatDate(a.due_date)}</td>
                      <td className="table-list-cell">{a.history_count}</td>
                      <td className="table-list-cell">{FMT(a.received_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {anchorState.length === 0 && (
                <NoDataFound
                  title="No anchor state yet"
                  message="Publish an event occurrence to seed the cache."
                />
              )}
            </div>
          </section>

          {/* N#1089 — Dead-letter queue management */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Dead-letter queue</h3>
                <div className="subtle">
                  Handler fires that exhausted the retry policy land here. Admin actions
                  (same idempotency keys), (no instance, period flagged
                  administratively handled), or <code>archive</code> .
                  Catch-up ticks <strong>do not</strong> auto-replay DLQ entries (BRD 8).
                </div>
              </div>
              <div className="engine-actions flex items-center gap-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <FormControl.SearchSelect
                  value={dlqFilter}
                  onChange={v => setDlqFilter((v as DlqStatus | 'all') ?? 'active')}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 're-fired', label: 'Re-fired' },
                    { value: 'waived', label: 'Waived' },
                    { value: 'archived', label: 'Archived' },
                    { value: 'all', label: 'All' },
                  ]}
                  className="filter-status"
                  noMargin
                  data-testid="SEL_engine_dlq_filter"
                />
                <Button variant="secondary-outline" title="Drain retry queue" data-testid="BTN_engine_dlq_drain_retry" disabled={retryBusy}
                  onClick={async () => {
                    setRetryBusy(true);
                    try {
                      const r = await processRetryQueue();
                      setInfo(`Drain ran: ${r.total} retrying row(s) attempted.`);
                      await load();
                    } catch (e: any) {
                      setError(extractApiError(e, 'Drain failed.'));
                    } finally {
                      setRetryBusy(false);
                    }
                  }}>
                  {retryBusy ? 'Draining…' : 'DRQ'}
                </Button>
              </div>
            </div>
            <div className="table-list">
              <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th">Queue #</th>
                    <th className="table-list-th">Rule</th>
                    <th className="table-list-th">Element</th>
                    <th className="table-list-th">Occ # / Retry</th>
                    <th className="table-list-th">DLQ status</th>
                    <th className="table-list-th">Last error</th>
                    <th className="table-list-th">Created</th>
                    <th className="table-list-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dlq.map(d => (
                    <tr key={d.id} className="table-list-row" style={
                      d.dlq_status === 'active' ? { background: '#fef2f2' } :
                        d.dlq_status === 're-fired' ? { background: '#ecfdf5' } :
                          undefined
                    }>
                      <td className="table-list-cell">#{d.id}</td>
                      <td className="table-list-cell">#{d.rule_id}</td>
                      <td className="table-list-cell">{d.scope_element_id ?? '—'}</td>
                      <td className="table-list-cell">{d.occurrence_number} / r{d.retry_count}</td>
                      <td className="table-list-cell">
                        <span className={'run-status s-' + (
                          d.dlq_status === 'active' ? 'lock-expired' :
                            d.dlq_status === 're-fired' ? 'succeeded' :
                              d.dlq_status === 'waived' ? 'partial' :
                                'skipped'
                        )}>
                          {d.dlq_status || '—'}
                        </span>
                      </td>
                      <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#b91c1c', maxWidth: 280 }}>
                        {d.error_code ? <><strong>{d.error_code}</strong>: </> : null}
                        {(d.last_error || '').slice(0, 140)}
                      </td>
                      <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>{FMT(d.created_on)}</td>
                      <td className="table-list-cell">
                        {d.dlq_status === 'active' ? (
                          <div style={{ display: 'inline-flex', gap: 4 }}>
                            <Button
                              variant="secondary-outline"
                              data-testid={`BTN_engine_dlq_row_${d.id}_refire`}
                              disabled={dlqBusy === d.id}
                              onClick={async () => {
                                const notes = window.prompt('Re-fire notes (optional):', '') ?? '';
                                setDlqBusy(d.id);
                                try {
                                  const r = await dlqRefire(d.id, notes);
                                  setInfo(`Re-fire ${r.outcome} — new queue #${r.new_queue_id}` + (r.instance_id ? `, instance #${r.instance_id}` : ''));
                                  await load();
                                } catch (e: any) {
                                  setError(extractApiError(e, 'Refire failed.'));
                                } finally {
                                  setDlqBusy(null);
                                }
                              }}>
                              Re-fire
                            </Button>
                            <Button
                              variant="update-outline"
                              data-testid={`BTN_engine_dlq_row_${d.id}_waive`}
                              disabled={dlqBusy === d.id}
                              onClick={async () => {
                                const notes = window.prompt('Waive reason (required):');
                                if (!notes || !notes.trim()) return;
                                setDlqBusy(d.id);
                                try {
                                  await dlqWaive(d.id, notes);
                                  setInfo(`Queue #${d.id} waived.`);
                                  await load();
                                } catch (e: any) {
                                  setError(extractApiError(e, 'Waive failed.'));
                                } finally {
                                  setDlqBusy(null);
                                }
                              }}>
                              Waive
                            </Button>
                            <Button
                              variant="delete-outline"
                              data-testid={`BTN_engine_dlq_row_${d.id}_archive`}
                              disabled={dlqBusy === d.id}
                              onClick={async () => {
                                const notes = window.prompt('Archive notes (optional):', '') ?? '';
                                setDlqBusy(d.id);
                                try {
                                  await dlqArchive(d.id, notes);
                                  setInfo(`Queue #${d.id} archived.`);
                                  await load();
                                } catch (e: any) {
                                  setError(extractApiError(e, 'Archive failed.'));
                                } finally {
                                  setDlqBusy(null);
                                }
                              }}>
                              Archive
                            </Button>
                          </div>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                            {d.dlq_action_notes ? d.dlq_action_notes.slice(0, 60) : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {dlq.length === 0 && (
                <NoDataFound
                  title={`No DLQ entries in ${dlqFilter}`}
                  message="No dead-letter queue entries to display."
                />
              )}
            </div>
          </section>

          {/* N#1095 — Catch-up attempts (audit-only view; operator-triggered via API) */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Catch-up attempts</h3>
                <div className="subtle">
                  Operator-triggered catch-up fires for missed periods. Version is
                  resolved by N#1090 with <code>is_catch_up=true</code> so the
                  <strong> historical</strong> Active version (at the
                  originally-scheduled fire date) drives the fire — even if the rule
                  has rolled forward since. BRD 5: materialised list is the
                  <strong>current</strong> scope (v1 simplification). Trigger via
                  <code> POST /api/v1/sch/rules/{`{rid}`}/catch-up</code>.
                </div>
              </div>
            </div>
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Rule</th>
                      <th className="table-list-th">Target date</th>
                      <th className="table-list-th">Reason</th>
                      <th className="table-list-th">Resolved version</th>
                      <th className="table-list-th">Outcome</th>
                      <th className="table-list-th">Scope</th>
                      <th className="table-list-th">Fired at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catchUpAttempts.map((a, idx) => (
                      <tr key={a.id} className="table-list-row" style={
                        a.outcome === 'fired' ? { background: '#f0fdf4' } :
                          a.outcome === 'partial' || a.outcome === 'error' ? { background: '#fef2f2' } :
                            { background: '#fef9c3' }
                      }>
                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                        <td className="table-list-cell">#{a.rule_id}</td>
                        <td className="table-list-cell">{formatDate(a.target_date)}</td>
                        <td className="table-list-cell" style={{ fontSize: '0.78rem' }}><code>{a.reason || '—'}</code></td>
                        <td className="table-list-cell" style={{ fontSize: '0.78rem' }}>
                          {a.resolved_version_number != null ? (
                            <>
                              <code>v{a.resolved_version_number}</code>{' '}
                              <span className={'run-source s-' + (a.resolved_source === 'snapshot' ? 'manual' : 'scheduled')}>
                                {a.resolved_source}
                              </span>
                            </>
                          ) : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td className="table-list-cell">
                          <span className={'run-status s-' + (
                            a.outcome === 'fired' ? 'succeeded' :
                              a.outcome === 'partial' ? 'partial' :
                                a.outcome === 'error' ? 'lock-expired' :
                                  a.outcome === 'no_active_version' ? 'partial' :
                                    'skipped'
                          )}>
                            {a.outcome}
                          </span>
                        </td>
                        <td className="table-list-cell" style={{ fontSize: '0.78rem', color: '#475569' }}>
                          {a.scope_elements_fired}/{a.scope_elements_attempted}
                          {a.scope_elements_errored > 0 && <span style={{ color: '#b91c1c' }}> (err {a.scope_elements_errored})</span>}
                        </td>
                        <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>{FMT(a.fired_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {catchUpAttempts.length === 0 && (
                <NoDataFound
                  title="No catch-up attempts recorded"
                  message="Operator-triggered catch-up fires will appear here."
                />
              )}
            </div>
          </section>

          {/* N#1094 — Mid-period transition inbox */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Mid-period version transitions
                  {midPeriod.length > 0 && <span style={{ marginLeft: 8, color: '#b91c1c', fontWeight: 700 }}>· {midPeriod.length} pending</span>}
                </h3>
                <div className="subtle">
                  When a new version becomes Active mid-period, scope deltas surface here as a binary
                  admin decision: <code>Backfill</code> (fire for the new scope elements in the
                  in-progress period) or <code>Skip</code> (new elements activate from the next period).
                  BRD 5: default is Skip — notifications stay until explicitly resolved.
                </div>
              </div>
            </div>
            <div className="table-list">
              <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Rule</th>
                    <th className="table-list-th">Versions</th>
                    <th className="table-list-th">Period</th>
                    <th className="table-list-th">Scope changes</th>
                    <th className="table-list-th">Detected</th>
                    <th className="table-list-th table-list-th--right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {midPeriod.map((t, idx) => (
                    <tr key={t.id} className="table-list-row" style={{ background: '#fef9c3' }}>
                      <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                      <td className="table-list-cell">#{t.rule_id}</td>
                      <td className="table-list-cell"><code>v{t.old_version_number} → v{t.new_version_number}</code></td>
                      <td className="table-list-cell" style={{ fontSize: '0.78rem', color: '#475569' }}>
                        <code>{t.period_token || '—'}</code><br />
                        <small>{formatDate(t.period_scheduled_date)}</small>
                      </td>
                      <td className="table-list-cell" style={{ fontSize: '0.78rem' }}>
                        {t.added_scope_element_ids.length > 0 && (
                          <div style={{ color: '#15803d' }}>
                            +{t.added_scope_element_ids.length}: {t.added_scope_element_ids.slice(0, 6).join(', ')}
                            {t.added_scope_element_ids.length > 6 && '…'}
                          </div>
                        )}
                        {t.removed_scope_element_ids.length > 0 && (
                          <div style={{ color: '#b91c1c' }}>
                            −{t.removed_scope_element_ids.length}: {t.removed_scope_element_ids.slice(0, 6).join(', ')}
                            {t.removed_scope_element_ids.length > 6 && '…'}
                          </div>
                        )}
                      </td>
                      <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>{FMT(t.detected_on)}</td>
                      <td className="table-list-cell table-list-cell--actions">
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <Button
                            variant="update"
                            data-testid={`BTN_engine_midperiod_row_${t.id}_backfill`}
                            disabled={midPeriodBusy === t.id || t.added_scope_element_ids.length === 0}
                            onClick={async () => {
                              const notes = window.prompt(`Backfill notes (will fire for ${t.added_scope_element_ids.length} scope element(s)):`, '') ?? '';
                              setMidPeriodBusy(t.id);
                              try {
                                const r = await backfillMidPeriodTransition(t.id, notes);
                                setInfo(`Backfilled — ${r.backfill_instance_ids.length} instance(s) created.`);
                                await load();
                              } catch (e: any) {
                                setError(extractApiError(e, 'Backfill failed.'));
                              } finally { setMidPeriodBusy(null); }
                            }}>
                            {midPeriodBusy === t.id ? 'Backfilling…' : `Backfill (${t.added_scope_element_ids.length})`}
                          </Button>
                          <Button
                            variant="update-outline"
                            data-testid={`BTN_engine_midperiod_row_${t.id}_skip`}
                            disabled={midPeriodBusy === t.id}
                            onClick={async () => {
                              const notes = window.prompt('Skip notes (optional):', '') ?? '';
                              setMidPeriodBusy(t.id);
                              try {
                                await skipMidPeriodTransition(t.id, notes);
                                setInfo(`Transition #${t.id} skipped.`);
                                await load();
                              } catch (e: any) {
                                setError(extractApiError(e, 'Skip failed.'));
                              } finally { setMidPeriodBusy(null); }
                            }}>
                            Skip
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {midPeriod.length === 0 && (
                <NoDataFound
                  title="No pending mid-period transitions"
                  message="When new versions become Active mid-period, scope deltas surface here."
                />
              )}
            </div>
          </section>

          {/* N#1088 — Handler registry */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Handler registry</h3>
                <div className="subtle">
                  N#1088 idempotency contract gate. Handlers register as
                  <code> pending</code>, prove BRDs 1, 3, 4 against the contract test,
                  then flip to <code>accepted</code>. Re-registration revokes the prior.
                </div>
              </div>
            </div>
            <div className="table-list">
              <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Endpoint</th>
                    <th className="table-list-th">Version</th>
                    <th className="table-list-th">Category / Type</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                    <th className="table-list-th">Accepted on</th>
                    <th className="table-list-th">Last test</th>
                    <th className="table-list-th table-list-th--right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {handlers.map((h, idx) => (
                    <tr key={h.id} className="table-list-row" style={h.status === 'accepted' ? undefined : { background: '#fef9c3' }}>
                      <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                      <td className="table-list-cell"><code>{h.handler_code}</code></td>
                      <td className="table-list-cell">{h.handler_endpoint || '—'}</td>
                      <td className="table-list-cell">{h.handler_version}</td>
                      <td className="table-list-cell">{h.category_id ?? '—'} / {h.type_id ?? '—'}</td>
                      <td className="table-list-cell">
                        <span className={'run-status s-' + (
                          h.status === 'accepted' ? 'succeeded' :
                            h.status === 'pending' ? 'partial' :
                              h.status === 'failed' ? 'lock-expired' :
                                'skipped'
                        )}>
                          {h.status}
                        </span>
                      </td>
                      <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>{FMT(h.accepted_on)}</td>
                      <td className="table-list-cell" style={{ fontSize: '0.74rem', color: '#64748b' }}>
                        {h.last_contract_test_id != null ? <code>#{h.last_contract_test_id}</code> : '—'}
                      </td>
                      <td className="table-list-cell table-list-cell--actions">
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <Button
                            variant="secondary-outline"
                            data-testid={`BTN_engine_handler_row_${h.id}_contract_test`}
                            disabled={handlerBusy === h.id}
                            onClick={async () => {
                              setHandlerBusy(h.id);
                              try {
                                const t = await runHandlerContractTest(h.id);
                                setInfo(`Contract test ${t.status} (retry=${t.retry_idempotent}, cross-source=${t.cross_source_idempotent}, ${t.duration_ms} ms).`);
                                await load();
                              } catch (e: any) {
                                setError(extractApiError(e, 'Contract test failed.'));
                              } finally {
                                setHandlerBusy(null);
                              }
                            }}>
                            {handlerBusy === h.id ? 'Testing…' : 'Run contract test'}
                          </Button>
                          {h.status === 'accepted' && (
                            <Button
                              variant="delete-outline"
                              data-testid={`BTN_engine_handler_row_${h.id}_revoke`}
                              disabled={handlerBusy === h.id}
                              onClick={async () => {
                                const reason = window.prompt('Revoke reason?');
                                if (!reason) return;
                                setHandlerBusy(h.id);
                                try {
                                  await revokeHandler(h.id, reason);
                                  setInfo(`Handler #${h.id} revoked.`);
                                  await load();
                                } catch (e: any) {
                                  setError(extractApiError(e, 'Revoke failed.'));
                                } finally {
                                  setHandlerBusy(null);
                                }
                              }}>
                              Revoke
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {handlers.length === 0 && (
                <NoDataFound
                  title="No handlers registered yet"
                  message="Register a handler to begin idempotency contract testing."
                />
              )}
            </div>
          </section>

          {/* Missed-tick log */}
          <section className="engine-section">
            <div className="engine-section-header">
              <div>
                <h3>Missed ticks</h3>
                <div className="subtle">Gaps detected on the next successful tick (BRD 8).</div>
              </div>
            </div>
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th">Missed at</th>
                      <th className="table-list-th">Detected at</th>
                      <th className="table-list-th">Policy</th>
                      <th className="table-list-th">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missed.map(m => (
                      <tr key={m.id} className="table-list-row">
                        <td className="table-list-cell">{FMT(m.missed_at)}</td>
                        <td className="table-list-cell">{FMT(m.detected_at)}</td>
                        <td className="table-list-cell">{m.policy_applied || '—'}</td>
                        <td className="table-list-cell">{m.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {missed.length === 0 && (
                <NoDataFound
                  title="No missed ticks recorded"
                  message="Gaps detected on the next successful tick will appear here."
                />
              )}
            </div>
          </section>
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default CadenceEnginePage;
